import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import clsx from 'clsx'
import CalendarIcon from '@ancon/wildcat-ui/shared/icons/calendar-event-outline.svg'

import useAppSelector from '../../../store/hooks/useAppSelector'
import { clientContextCurrentUserCompanyNameSelector } from '../../clientContext/store/clientContextSelectors'
import DropdownIcon from '../../app/components/DropdownIcon'
import PopoverPanel from '../../app/components/popover/PopoverPanel'
import usePopover from '../../app/hooks/usePopover'
import { CompanyRoutes } from '../constants'

import ProfileAvatar from './ProfileAvatar'
import ProfileSwitcherOptions from './ProfileSwitcherOptions'
import styles from './CompanyContextBanner.module.scss'

type CompanyContextBannerProps = {
  className?: string
}

/** "You're ordering on behalf of <Company>", with a way out of it */
export default function CompanyContextBanner({
  className,
}: CompanyContextBannerProps) {
  const { t } = useTranslation('common')
  const router = useRouter()

  const companyName = useAppSelector(
    clientContextCurrentUserCompanyNameSelector,
  )
  const { isOpen, close, containerRef, triggerRef, triggerProps, popoverId } =
    usePopover()

  if (!companyName) {
    return null
  }

  return (
    <section
      className={clsx(
        styles.banner,
        { [styles.bannerOpen]: isOpen },
        className,
      )}
      aria-label={t('identity.companyContext')}
    >
      <ProfileAvatar name={companyName} isCompany size="small" />
      <span className={styles.text}>
        {t('identity.orderingOnBehalfOf')} <strong>{companyName}</strong>
      </span>
      <span className={styles.actions}>
        <button
          type="button"
          className={styles.button}
          onClick={() => router.push(CompanyRoutes.Overview)}
        >
          <CalendarIcon aria-hidden />
          {t('identity.companyOverview')}
        </button>
        <span className={styles.switcher} ref={containerRef}>
          <button
            type="button"
            ref={triggerRef}
            className={clsx(styles.button, styles.solid)}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...triggerProps}
          >
            {t('identity.switchProfile')}
            <DropdownIcon isOpen={isOpen} />
          </button>
          {isOpen && (
            <PopoverPanel id={popoverId}>
              <ProfileSwitcherOptions onDone={close} hideCompanyLinks />
            </PopoverPanel>
          )}
        </span>
      </span>
    </section>
  )
}
